import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { 
  AlertTriangle, 
  Heart, 
  Sparkles, 
  RefreshCw, 
  ChevronDown, 
  ChevronUp,
  Info,
  X,
  CheckCircle2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

// Gray Words Dictionary - words that block genuine communication
// Categorized by type of communication violence
const GRAY_WORDS_DICTIONARY = {
  moralizing: {
    words: ['should', 'shouldn\'t', 'must', 'mustn\'t', 'have to', 'need to', 'ought to', 'supposed to', 'obligated', 'duty', 'required'],
    description: 'Moralizing imposes your values on others, creating resistance instead of connection.',
    alternative: 'Try expressing your feelings and needs instead: "I feel... because I need..."'
  },
  judging: {
    words: ['wrong', 'right', 'bad', 'good', 'stupid', 'smart', 'lazy', 'selfish', 'rude', 'mean', 'cruel', 'thoughtless', 'careless', 'irresponsible', 'immature', 'childish', 'pathetic', 'disgusting', 'ridiculous', 'absurd'],
    description: 'Judgments label and categorize, blocking our ability to see the human behind the behavior.',
    alternative: 'Describe the specific behavior you observed and how it affected you.'
  },
  blaming: {
    words: ['your fault', 'you made me', 'because of you', 'you always', 'you never', 'you\'re the reason', 'blame', 'caused'],
    description: 'Blame creates defensiveness and prevents the other person from hearing your pain.',
    alternative: 'Take ownership of your feelings: "When X happened, I felt Y because I needed Z."'
  },
  demanding: {
    words: ['you better', 'you\'d better', 'or else', 'i demand', 'i insist', 'do it now', 'immediately', 'right now', 'i expect you to'],
    description: 'Demands trigger resistance. Even reasonable requests become harder to hear when wrapped in demand energy.',
    alternative: 'Make requests, not demands. A request can be declined without punishment.'
  },
  advising: {
    words: ['you should try', 'why don\'t you', 'if i were you', 'the best thing to do', 'you need to just', 'have you tried', 'you could always', 'my advice', 'let me tell you'],
    description: 'Unsolicited advice, even when correct, often blocks connection. People want to be heard first.',
    alternative: 'First, empathize fully. Then ask: "Would you like to hear my thoughts?"'
  },
  comparing: {
    words: ['unlike you', 'better than', 'worse than', 'at least', 'compared to', 'more than you', 'less than', 'not like'],
    description: 'Comparisons create separation and competition instead of understanding.',
    alternative: 'Focus on this person, this moment, this unique experience.'
  },
  denying_responsibility: {
    words: ['i had to', 'i had no choice', 'they made me', 'it\'s just how i am', 'i can\'t help it', 'that\'s just me'],
    description: 'Denying our agency disconnects us from our power to choose our responses.',
    alternative: 'Own your choices: "I chose to... because I valued..."'
  },
  threatening: {
    words: ['if you don\'t', 'you\'ll regret', 'watch out', 'be careful or', 'i\'m warning you', 'don\'t make me'],
    description: 'Threats use fear to control, destroying trust and safety.',
    alternative: 'Express your needs and the natural consequences you see, not punishments.'
  },
  labeling: {
    words: ['you are a', 'you\'re such a', 'typical', 'that\'s so you', 'narcissist', 'liar', 'manipulator', 'abuser', 'toxic'],
    description: 'Labels reduce a whole human being to a single characteristic.',
    alternative: 'Describe specific behaviors and their impact on you.'
  },
  absolutes: {
    words: ['always', 'never', 'every time', 'nothing', 'everything', 'everyone', 'no one', 'constantly', 'forever'],
    description: 'Absolutes are rarely accurate and make the other person feel unseen and defensive.',
    alternative: 'Be specific: "In this instance..." or "Sometimes when..."'
  }
};

// Flatten all gray words for quick lookup
const ALL_GRAY_WORDS = Object.values(GRAY_WORDS_DICTIONARY).flatMap(cat => cat.words);

// The grandmother wisdom - the spirit of genuine curiosity (inspired by M.ai.ry Memories)
const GRANDMOTHER_WISDOM = [
  "Like a grandmother who asks 'How are you?' with no other concern but your wellbeing...",
  "When you are genuine, the other person will become genuine. It's the magic.",
  "Genuineness is the superpower. It creates a field where truth can emerge.",
  "Before anything else - pause until you feel genuine curiosity about the other person.",
  "The goal is not to be nice. The goal is to be real, with care.",
  "Even if you're right, even if they're wrong - start from love anyway.",
  "This isn't about winning. It's about understanding.",
  "Can you hold curiosity for the human beneath the behavior?",
  "Help us remember to belong to each other again.",
  "Belonging first. Everything else flows.",
  "Trust is currency; regeneration is the economy; love is the organizing principle.",
  "Coherence before content. Find your calm first, speak second.",
  "The less blame and criticism, the easier it is for others to hear us.",
  "When people are under duress, they need genuine curiosity and care first.",
  "People are more likely to listen when they feel heard. Reflect before you respond.",
  "We have more clarity and power when we use fewer words with more sincerity.",
  "The goal isn't to be perfect. It's to create genuine connection where everyone wins."
];

// Function to analyze text for gray words
export const analyzeForGrayWords = (text) => {
  if (!text || typeof text !== 'string') return { found: [], score: 100, isClean: true };
  
  const lowerText = text.toLowerCase();
  const found = [];
  
  Object.entries(GRAY_WORDS_DICTIONARY).forEach(([category, data]) => {
    data.words.forEach(word => {
      const regex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      const matches = text.match(regex);
      if (matches) {
        found.push({
          word,
          category,
          description: data.description,
          alternative: data.alternative,
          count: matches.length
        });
      }
    });
  });
  
  // Score: 100 is clean, lower = more gray words found
  const totalGray = found.reduce((sum, f) => sum + f.count, 0);
  const wordCount = text.split(/\s+/).filter(Boolean).length;
  const score = wordCount > 0 ? Math.max(0, 100 - (totalGray / wordCount) * 100 * 5) : 100;
  
  return {
    found,
    score: Math.round(score),
    isClean: found.length === 0,
    totalGray,
    wordCount
  };
};

// Function to highlight gray words in text
export const highlightGrayWords = (text) => {
  if (!text) return text;
  
  let highlighted = text;
  ALL_GRAY_WORDS.forEach(word => {
    const regex = new RegExp(`\\b(${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    highlighted = highlighted.replace(regex, '<span class="gray-word-highlight">$1</span>');
  });
  
  return highlighted;
};

// Pre-reflection component - shown before user starts expressing feelings
export const GrayWordsPreflection = ({ isOpen, onClose, onProceed }) => {
  const [currentWisdom, setCurrentWisdom] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      const interval = setInterval(() => {
        setCurrentWisdom(prev => (prev + 1) % GRANDMOTHER_WISDOM.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-zinc-900/95 backdrop-blur-xl border-zinc-700/50 max-w-2xl"
        data-testid="gray-words-preflection-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-amber-400 flex items-center gap-2">
            <Eye className="w-6 h-6" />
            Before You Speak...
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            A moment of awareness for more effective communication
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Grandmother Wisdom Quote */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-transparent to-amber-500/10 rounded-lg" />
            <div 
              className="relative p-6 text-center italic text-lg text-amber-200/90 transition-opacity duration-500"
              data-testid="grandmother-wisdom-quote"
            >
              "{GRANDMOTHER_WISDOM[currentWisdom]}"
            </div>
            <div className="flex justify-center gap-1 mt-2">
              {GRANDMOTHER_WISDOM.map((_, i) => (
                <div 
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    i === currentWisdom ? 'bg-amber-400' : 'bg-zinc-600'
                  }`}
                />
              ))}
            </div>
          </div>
          
          {/* Gray Words Explanation */}
          <Card className="bg-zinc-800/50 border-zinc-700/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-zinc-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-zinc-500" />
                The Color of Non-Connection: Gray
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-zinc-400 space-y-3">
              <p>
                <span className="text-zinc-300">Gray words</span> are the language of non-belonging — 
                words that, even when well-intended or correct, tend to <span className="text-zinc-300">block communication</span>.
              </p>
              <p>
                These include anything that forces something upon the other person: 
                <span className="text-zinc-500 mx-1">moralizing, judging, critiquing, advising, demanding</span> — 
                all of this blocks the field of genuine connection.
              </p>
              <p className="text-amber-300/80">
                If you have not started from a place of shared consciousness, a genuine concern and curiosity, 
                even the best advice falls on deaf ears.
              </p>
            </CardContent>
          </Card>
          
          {/* Acknowledgment */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAcknowledged(!acknowledged)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                acknowledged 
                  ? 'bg-amber-500 border-amber-500' 
                  : 'border-zinc-600 hover:border-zinc-500'
              }`}
              data-testid="acknowledge-checkbox"
            >
              {acknowledged && <CheckCircle2 className="w-4 h-4 text-black" />}
            </button>
            <span className="text-sm text-zinc-300">
              I will pause to check: Am I being genuinely curious, like a grandmother?
            </span>
          </div>
          
          {/* Proceed Button */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-300"
              data-testid="preflection-skip-btn"
            >
              Skip
            </Button>
            <Button
              onClick={() => {
                onProceed();
                if (!acknowledged) {
                  toast.info("Remember: genuine curiosity is the key", { duration: 3000 });
                }
              }}
              className="bg-amber-600 hover:bg-amber-500 text-black"
              data-testid="preflection-proceed-btn"
            >
              <Heart className="w-4 h-4 mr-2" />
              Enter with Genuine Curiosity
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Inline analyzer component - shows while user is typing
export const GrayWordsInlineAnalyzer = ({ text, showDetails = false }) => {
  const analysis = useMemo(() => analyzeForGrayWords(text), [text]);
  const [expanded, setExpanded] = useState(false);
  
  if (analysis.isClean || !text || text.length < 5) {
    return null;
  }
  
  return (
    <div 
      className="mt-2 p-3 rounded-lg bg-zinc-800/60 border border-zinc-700/50"
      data-testid="gray-words-inline-analyzer"
    >
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-zinc-600/50 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-zinc-400" />
          </div>
          <div>
            <span className="text-sm text-zinc-400">
              {analysis.found.length} gray word{analysis.found.length !== 1 ? 's' : ''} detected
            </span>
            <span className="text-xs text-zinc-500 ml-2">
              (may block connection)
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className={`border-zinc-600 ${
              analysis.score > 70 ? 'text-emerald-400' : 
              analysis.score > 40 ? 'text-amber-400' : 'text-red-400'
            }`}
          >
            {analysis.score}% clear
          </Badge>
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </div>
      
      {expanded && (
        <div className="mt-3 space-y-2 border-t border-zinc-700/50 pt-3">
          {analysis.found.map((item, idx) => (
            <div key={idx} className="text-sm">
              <div className="flex items-center gap-2">
                <Badge className="bg-zinc-600/50 text-zinc-300 font-mono text-xs">
                  "{item.word}"
                </Badge>
                <span className="text-zinc-500 text-xs capitalize">
                  ({item.category.replace('_', ' ')})
                </span>
              </div>
              {showDetails && (
                <div className="ml-4 mt-1 text-xs text-zinc-500">
                  <p className="text-zinc-400">{item.description}</p>
                  <p className="text-emerald-400/70 mt-1">→ {item.alternative}</p>
                </div>
              )}
            </div>
          ))}
          
          {!showDetails && (
            <p className="text-xs text-zinc-500 italic mt-2">
              Tap on each word for guidance on alternatives
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Full analyzer panel - for dedicated analysis step
export const GrayWordsFullAnalyzer = ({ onComplete }) => {
  const [text, setText] = useState('');
  const [showingAlternatives, setShowingAlternatives] = useState(null);
  const analysis = useMemo(() => analyzeForGrayWords(text), [text]);
  
  return (
    <Card 
      className="bg-zinc-900/80 border-zinc-700/50 backdrop-blur-sm"
      data-testid="gray-words-full-analyzer"
    >
      <CardHeader>
        <CardTitle className="font-serif text-xl text-zinc-200 flex items-center gap-2">
          <Eye className="w-5 h-5 text-zinc-400" />
          Language Awareness Check
        </CardTitle>
        <p className="text-sm text-zinc-400">
          Before expressing your feelings, take a moment to notice your language patterns.
        </p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm text-zinc-400 mb-2 block">
            What are you wanting to say? (Draft your thoughts here)
          </label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type what you're thinking or feeling... We'll gently highlight any words that might block connection."
            className="min-h-[120px] bg-zinc-800/50 border-zinc-700 text-zinc-200 placeholder:text-zinc-600"
            data-testid="gray-words-input"
          />
        </div>
        
        {/* Analysis Results */}
        {text.length > 10 && (
          <div className="space-y-3">
            {/* Score Badge */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Connection-Readiness Score:</span>
              <div className="flex items-center gap-2">
                <div 
                  className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden"
                  data-testid="gray-words-score-bar"
                >
                  <div 
                    className={`h-full transition-all duration-500 ${
                      analysis.score > 70 ? 'bg-emerald-500' :
                      analysis.score > 40 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${analysis.score}%` }}
                  />
                </div>
                <span className={`text-sm font-medium ${
                  analysis.score > 70 ? 'text-emerald-400' :
                  analysis.score > 40 ? 'text-amber-400' : 'text-red-400'
                }`}>
                  {analysis.score}%
                </span>
              </div>
            </div>
            
            {/* Gray Words Found */}
            {analysis.found.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-zinc-500 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Words that may block genuine connection:
                </p>
                <div className="flex flex-wrap gap-2">
                  {analysis.found.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => setShowingAlternatives(showingAlternatives === idx ? null : idx)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                        showingAlternatives === idx
                          ? 'bg-zinc-600 border-amber-500/50 text-amber-300'
                          : 'bg-zinc-700/50 border-zinc-600 text-zinc-400 hover:border-zinc-500'
                      }`}
                      data-testid={`gray-word-chip-${item.word.replace(/\s+/g, '-')}`}
                    >
                      <span className="font-mono">"{item.word}"</span>
                      {item.count > 1 && (
                        <span className="ml-1 text-xs text-zinc-500">×{item.count}</span>
                      )}
                    </button>
                  ))}
                </div>
                
                {/* Alternative Suggestion Panel */}
                {showingAlternatives !== null && analysis.found[showingAlternatives] && (
                  <div 
                    className="p-4 rounded-lg bg-zinc-800/70 border border-zinc-700/50 mt-3"
                    data-testid="alternative-suggestion-panel"
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <p className="text-xs text-zinc-500 uppercase tracking-wide">
                          {analysis.found[showingAlternatives].category.replace('_', ' ')}
                        </p>
                        <p className="text-sm text-zinc-300">
                          {analysis.found[showingAlternatives].description}
                        </p>
                        <p className="text-sm text-emerald-400/80 flex items-start gap-2">
                          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
                          {analysis.found[showingAlternatives].alternative}
                        </p>
                      </div>
                      <button 
                        onClick={() => setShowingAlternatives(null)}
                        className="text-zinc-500 hover:text-zinc-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm">Your language feels open and connective!</span>
              </div>
            )}
          </div>
        )}
        
        {/* Guidance Note */}
        <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
          <p className="text-xs text-amber-300/70 leading-relaxed">
            <strong>Remember:</strong> This isn't about being "right" or "wrong" — it's about 
            <em> effectiveness</em>. Gray words often block communication even when they're accurate. 
            The goal is genuine curiosity, like a grandmother asking how you are, with no other agenda.
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-between pt-2">
          <Button
            variant="ghost"
            onClick={() => setText('')}
            className="text-zinc-400 hover:text-zinc-300"
            data-testid="clear-analyzer-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Clear & Restart
          </Button>
          <Button
            onClick={onComplete}
            className="bg-amber-600 hover:bg-amber-500 text-black"
            data-testid="analyzer-proceed-btn"
          >
            <Heart className="w-4 h-4 mr-2" />
            Continue with Awareness
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Export all utilities
export { GRAY_WORDS_DICTIONARY, ALL_GRAY_WORDS, GRANDMOTHER_WISDOM };

export default GrayWordsFullAnalyzer;
